import { Button, Modal } from '@shopify/polaris';
import { ControlBarStyled } from './styled';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useEventListener from '@/hooks/useEventListener';
import { config } from '@/config';
import { SaveBar } from '@shopify/app-bridge-react';
import { OTElementId } from '@/constants';
import useHandleContextureBar from '@/hooks/useHandleContextureBar';
import { useDispatch, useSelector } from 'react-redux';
import contextualBarSlice, { confirmNavigateSelector } from '@/redux/features/contextualBar.slice';
import { useNavigate } from 'react-router-dom';

interface Props {
  hidden: boolean;
  actionGroup: Array<{
    content: string;
    onAction: () => void;
    primary?: boolean;
    disabled?: boolean;
    loading?: boolean;
  }>;
  changeFields?: any;
  isFixed?: boolean;
  // Unique id for this save bar. Required when more than one ControlBar can be
  // mounted on the same screen (e.g. the Courier Mapping tab renders two) — a
  // duplicated id makes App Bridge disable the Save button on the second bar.
  saveBarId?: string;
}

const ControlBar = ({ actionGroup, hidden, changeFields, isFixed, saveBarId = OTElementId.SaveBar }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const contextualBar = useHandleContextureBar();
  // Keep the latest show/hide without putting the (re-created each render) object
  // in the effect deps, so the effect only runs when `hidden` actually changes.
  const contextualBarRef = useRef(contextualBar);
  contextualBarRef.current = contextualBar;
  const confirmNavigateState = useSelector(confirmNavigateSelector);
  let ctrlOrCommandPressed = false;
  const [isShowModalDiscard, setIsShowModalDiscard] = useState(false);

  const alertUser = (e: any) => {
    e.preventDefault();
    e.returnValue = 'Changes you made may not be saved.';
  };

  const handKeyDownSearchOnPage = (e: KeyboardEvent) => {
    if (e.ctrlKey || e.key === 'Meta') {
      ctrlOrCommandPressed = true;
    }
    if (ctrlOrCommandPressed && e.key === 's') {
      e.preventDefault();
      ctrlOrCommandPressed = false;
      const saveAction: any = actionGroup.find((item) => item.content === 'Save' || item.primary);
      return saveAction.onAction();
    }
  };

  useEventListener('keydown', handKeyDownSearchOnPage);

  useEffect(() => {
    if (changeFields) {
      window.addEventListener('beforeunload', alertUser);
    } else {
      window.removeEventListener('beforeunload', alertUser);
    }

    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeFields]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hidden) {
        dispatch(contextualBarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: true }));
        contextualBarRef.current.show(saveBarId);
      } else {
        dispatch(contextualBarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: false }));
        contextualBarRef.current.hide(saveBarId);
      }
    }, 200);
    // Clean up the pending timer so rapid re-renders can't pile up overlapping
    // show()/hide() calls (root cause of the App Bridge save-bar render loop).
    return () => clearTimeout(timer);
  }, [dispatch, hidden, saveBarId]);

  // Release the shared save-bar state when this form unmounts. Without it, a form
  // left while dirty (e.g. navigating away via the sidebar instead of the confirm
  // modal) strands `isShowContextualBar` true globally — then useConfirmationNavigate
  // blocks re-entry on pages that mount no ControlBar to render the confirm modal
  // (the "can't reopen the edit page" bug). Also clears any pending confirm dialog
  // and hides the App Bridge save bar so nothing lingers on the next page.
  useEffect(() => {
    return () => {
      dispatch(contextualBarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: false }));
      dispatch(contextualBarSlice.actions.handleConfirmNavigateState({ isShowConfirmNavigate: false }));
      contextualBarRef.current.hide(saveBarId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // App Bridge's <ui-save-bar> exposes discardConfirmation as a DOM property, not
  // a string attribute. Passing it as a JSX boolean made React 18 render
  // discardconfirmation="true", which App Bridge rejects ("Unexpected value...")
  // on every render. Set it via the element property instead.
  const setSaveBarRef = useCallback((element: HTMLElement | null) => {
    if (element) (element as unknown as { discardConfirmation: boolean }).discardConfirmation = true;
  }, []);

  const contextualBarElement = useMemo(() => {
    return config.embedded !== '1' ? (
      !hidden ? (
        <ControlBarStyled isFixed={isFixed}>
          {actionGroup.map((item, index) => {
            return (
              <Button onClick={item.onAction} {...item} key={index}>
                {item.content}
              </Button>
            );
          })}
        </ControlBarStyled>
      ) : null
    ) : (
      <SaveBar id={saveBarId} ref={setSaveBarRef}>
        {actionGroup.map((item, index) => (
          <button
            key={index}
            onClick={item.onAction}
            variant={item.primary ? 'primary' : undefined}
            disabled={item.disabled}
            loading={item.loading ? '' : false}
          >
            {item.content}
          </button>
        ))}
      </SaveBar>
    );
  }, [actionGroup, hidden, isFixed, setSaveBarRef, saveBarId]);

  return (
    <>
      {contextualBarElement}
      <Modal
        title="Discard all unsaved changes"
        open={isShowModalDiscard}
        onClose={() => setIsShowModalDiscard(false)}
        primaryAction={{
          content: 'Discard changes',
          onAction: () => {
            actionGroup.find((item) => !item.primary)?.onAction();
            setIsShowModalDiscard(false);
          },
          // @ts-ignore
          tone: 'critical',
        }}
        secondaryActions={[{ content: 'Continue editing', onAction: () => setIsShowModalDiscard(false) }]}
      >
        <Modal.Section>If you discard changes, you’ll delete any edits you made since you last saved.</Modal.Section>
      </Modal>
      <Modal
        title="You have unsaved changes"
        open={confirmNavigateState.isShowConfirmNavigate}
        onClose={() => {
          dispatch(contextualBarSlice.actions.handleConfirmNavigateState({ isShowConfirmNavigate: false }));
        }}
        primaryAction={{
          content: 'Leave page',
          onAction: () => {
            if (!confirmNavigateState.to) return;
            // Hide the save bar BEFORE navigating, while <ui-save-bar> is still
            // mounted. App Bridge's bar lives in the top Admin frame, so hide(id)
            // run during unmount (after the element is gone) throws "not found" and
            // the bar lingers on the next page. Hiding here clears it reliably.
            contextualBarRef.current.hide(saveBarId);
            navigate(confirmNavigateState.to, confirmNavigateState.options);
            actionGroup.find((item) => !item.primary)?.onAction();
            dispatch(contextualBarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: false }));
            dispatch(contextualBarSlice.actions.handleConfirmNavigateState({ isShowConfirmNavigate: false }));
          },
          // @ts-ignore
          tone: 'critical',
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              dispatch(contextualBarSlice.actions.handleConfirmNavigateState({ isShowConfirmNavigate: false }));
            },
          },
        ]}
      >
        <Modal.Section>If you leave this page, all unsaved changes will be lost.</Modal.Section>
      </Modal>
    </>
  );
};

export default memo(ControlBar);
